/**
 * Admin page (/admin/players): browse accounts (search by name, email or id), then one player's
 * stat sheet and every counted game they played, each linking to its log (`/admin/games?id=`).
 * `?id=` opens a player directly.
 */
import { useEffect, useState } from 'react';
import { METRICS, METRIC_KINDS, t, type AdminPlayerDetail, type AdminPlayerSummary } from '@kadice/shared';
import { api } from '../api';
import { formatMetric, metricName } from './Leaderboard';

const when = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : t('admin.players.never'));
const errorText = (err: unknown) => (err instanceof Error ? err.message : t('admin.failed'));
const rivals = (g: AdminPlayerDetail['history'][number]) => g.opponents.map((o) => o.name).join(', ');

export function PlayerAdmin() {
  const [q, setQ] = useState('');
  const [list, setList] = useState<AdminPlayerSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<number | null>(
    () => Number(new URLSearchParams(location.search).get('id')) || null,
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      api.listPlayers(q).then(setList, (err: unknown) => setError(errorText(err)));
    }, 250);
    return () => clearTimeout(timer);
  }, [q]);

  return (
    <div className="admin-feedback">
      <header className="row">
        <h2>{t('admin.players')}</h2>
        <span className="spacer" />
        <a href="/admin/games">{t('admin.players.link.games')}</a>
        <a href="/admin/feedback">{t('admin.players.link.feedback')}</a>
        <a href="/">{t('admin.players.link.back')}</a>
      </header>
      {error && <p className="error">{error}</p>}
      <div className="admin-feedback-body">
        <div>
          <input
            type="search"
            value={q}
            placeholder={t('admin.players.search')}
            aria-label={t('admin.players.search')}
            onChange={(e) => setQ(e.target.value)}
          />
          {list?.length === 0 && <p className="muted">{t('admin.players.none')}</p>}
          <ul className="admin-feedback-list">
            {list?.map((p) => (
              <li key={p.id}>
                <button className={openId === p.id ? 'open' : ''} onClick={() => setOpenId(p.id)}>
                  <span>
                    <strong>{p.displayName}</strong>
                    {p.isAdmin && <span className="muted small"> {t('admin.players.admin')}</span>}
                  </span>
                  <span className="muted small">
                    {t('admin.players.row', { id: p.id, games: p.games, wins: p.wins, played: when(p.lastPlayedAt) })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </div>
        {openId !== null && <Player key={openId} id={openId} />}
      </div>
    </div>
  );
}

function Player({ id }: { id: number }) {
  const [p, setP] = useState<AdminPlayerDetail | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.getPlayer(id).then(setP, (err: unknown) => setError(errorText(err)));
  }, [id]);

  if (error) return <p className="error">{error}</p>;
  if (!p) return <p className="muted">{t('admin.loading')}</p>;
  const a = p.aggregate;

  return (
    <article className="admin-feedback-report">
      <h3>{p.displayName}</h3>
      <p className="muted small">
        {t('admin.players.detail', {
          id: p.id,
          email: p.email ?? t('admin.players.noEmail'),
          joined: when(p.createdAt),
          login: when(p.lastLoginAt),
        })}
      </p>
      {a.games === 0 ? (
        <p className="muted">{t('admin.players.noGames')}</p>
      ) : (
        <>
          <h4>{t('admin.players.stats')}</h4>
          <div className="admin-gamelog-stats">
            {METRIC_KINDS.map((kind) => (
              <table key={kind}>
                <caption>{t(`stats.tab.${kind}`)}</caption>
                <tbody>
                  {METRICS.filter((m) => m.kind === kind).map((m) => {
                    const v = m.value(a);
                    return (
                      <tr key={m.id} title={t(`stats.metric.${m.id}.hint`)}>
                        <th>{metricName(m.id)}</th>
                        <td>{v === null ? t('stats.none') : formatMetric(m.id, v)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ))}
          </div>
          <h4>{t('admin.players.history')}</h4>
          <div className="admin-gamelog-stats">
            <table>
              <thead>
                <tr>
                  <th>{t('admin.players.col.game')}</th>
                  <th>{t('admin.players.col.finished')}</th>
                  <th>{t('admin.players.col.result')}</th>
                  <th>{t('admin.players.col.against')}</th>
                  <th>{t('admin.players.col.kills')}</th>
                  <th>{t('admin.players.col.attacks')}</th>
                  <th>{t('admin.players.col.rounds')}</th>
                  <th>{t('admin.players.col.length')}</th>
                </tr>
              </thead>
              <tbody>
                {p.history.map((g) => (
                  <tr key={g.gameId}>
                    <td>
                      <a href={`/admin/games?id=${g.gameId}`}>{g.gameId}</a>
                    </td>
                    <td>{when(g.finishedAt)}</td>
                    <td>
                      {g.won ? t('admin.players.won') : t('admin.players.place', { n: g.placement, of: g.seats })}
                      {g.forfeited && ` ${t('admin.players.forfeited')}`}
                    </td>
                    <td>{rivals(g)}</td>
                    <td>{g.counters.kills}</td>
                    <td>{g.counters.attacks}</td>
                    <td>{g.counters.rounds}</td>
                    <td>{formatMetric('best.durationMs', g.counters.durationMs)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </article>
  );
}
